import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import api from "../Api";

const emptyAccount = {
  name: "",
  platform_code: "",
  platform_name: "",
  account_label: "",
  courier_code: "",
  courier_name: "",
};

const emptyRule = {
  type: "platform",
  code: "",
  label: "",
};

const AccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [abbreviations, setAbbreviations] = useState([]);
  const [newAccount, setNewAccount] = useState(emptyAccount);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyAccount);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [editingRuleId, setEditingRuleId] = useState(null);

  const platformRules = useMemo(
    () => abbreviations.filter(rule => rule.type === "platform"),
    [abbreviations]
  );
  const courierRules = useMemo(
    () => abbreviations.filter(rule => rule.type === "courier"),
    [abbreviations]
  );

  const fetchAccounts = async () => {
    const res = await api.get("/api/accounts");
    setAccounts(res.data);
  };

  const fetchAbbreviations = async () => {
    const res = await api.get("/api/account-abbreviations");
    setAbbreviations(res.data);
  };

  const refresh = async () => {
    await Promise.all([fetchAccounts(), fetchAbbreviations()]);
  };

  useEffect(() => {
    refresh();
  }, []);

  const getRuleLabel = (type, code) => {
    const rules = type === "platform" ? platformRules : courierRules;
    return rules.find(rule => rule.code === code)?.label || "";
  };

  const updateHierarchyField = (setter, form, field, value) => {
    const next = { ...form, [field]: value };
    if (field === "platform_code") next.platform_name = getRuleLabel("platform", value);
    if (field === "courier_code") next.courier_name = getRuleLabel("courier", value);
    setter(next);
  };

  const inferFromName = async (form, setter) => {
    if (!form.name.trim()) return;
    const res = await api.post("/api/accounts/infer", { name: form.name });
    setter({ ...form, ...res.data });
  };

  const addAccount = async () => {
    if (!newAccount.name.trim()) return;
    await api.post("/api/accounts", newAccount);
    setNewAccount(emptyAccount);
    await fetchAccounts();
  };

  const startEdit = (account) => {
    setEditingId(account.id);
    setEditForm({
      name: account.name || "",
      platform_code: account.platform_code || "",
      platform_name: account.platform_name || "",
      account_label: account.account_label || "",
      courier_code: account.courier_code || "",
      courier_name: account.courier_name || "",
    });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim()) return;
    await api.put(`/api/accounts/${id}`, editForm);
    setEditingId(null);
    setEditForm(emptyAccount);
    await fetchAccounts();
  };

  const toggleActive = async (id, currentState, name) => {
    const action = currentState ? "deactivate" : "activate";
    const ok = window.confirm(
      `${action} "${name}"? Existing order and print data will be preserved.`
    );
    if (!ok) return;
    const res = await api.patch(`/api/accounts/${id}/toggle-active`);
    alert(res.data.message);
    await fetchAccounts();
  };

  const removeAccount = async (id, name) => {
    const ok = window.confirm(
      `Delete "${name}" permanently? This only works if there are no orders or prints.`
    );
    if (!ok) return;

    try {
      await api.delete(`/api/accounts/${id}`);
      await fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete account.");
    }
  };

  const saveRule = async () => {
    if (!ruleForm.code.trim() || !ruleForm.label.trim()) return;
    if (editingRuleId) {
      await api.put(`/api/account-abbreviations/${editingRuleId}`, ruleForm);
    } else {
      await api.post("/api/account-abbreviations", ruleForm);
    }
    setRuleForm(emptyRule);
    setEditingRuleId(null);
    await fetchAbbreviations();
  };

  const editRule = (rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({ type: rule.type, code: rule.code, label: rule.label });
  };

  const deleteRule = async (rule) => {
    const ok = window.confirm(`Delete abbreviation ${rule.code} -> ${rule.label}?`);
    if (!ok) return;
    await api.delete(`/api/account-abbreviations/${rule.id}`);
    await fetchAbbreviations();
  };

  const renderHierarchyInputs = (form, setter, compact = false) => (
    <Stack direction={compact ? "column" : "row"} spacing={1} sx={{ minWidth: compact ? 160 : "auto" }}>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Platform</InputLabel>
        <Select
          label="Platform"
          value={form.platform_code || ""}
          onChange={(e) => updateHierarchyField(setter, form, "platform_code", e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {platformRules.map(rule => (
            <MenuItem key={rule.id} value={rule.code}>{rule.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Main Account"
        value={form.account_label || ""}
        onChange={(e) => setter({ ...form, account_label: e.target.value })}
        sx={{ minWidth: 150 }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Courier</InputLabel>
        <Select
          label="Courier"
          value={form.courier_code || ""}
          onChange={(e) => updateHierarchyField(setter, form, "courier_code", e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {courierRules.map(rule => (
            <MenuItem key={rule.id} value={rule.code}>{rule.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
        Accounts
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
          Add Account
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Operational Name"
            value={newAccount.name}
            onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && addAccount()}
            sx={{ minWidth: 190 }}
          />
          <Button variant="outlined" onClick={() => inferFromName(newAccount, setNewAccount)}>
            Use Name Rules
          </Button>
          {renderHierarchyInputs(newAccount, setNewAccount)}
          <Button variant="contained" onClick={addAccount}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
          Abbreviations
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={ruleForm.type}
              onChange={(e) => setRuleForm({ ...ruleForm, type: e.target.value })}
            >
              <MenuItem value="platform">Platform</MenuItem>
              <MenuItem value="courier">Courier</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Code"
            value={ruleForm.code}
            onChange={(e) => setRuleForm({ ...ruleForm, code: e.target.value.toUpperCase() })}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Name"
            value={ruleForm.label}
            onChange={(e) => setRuleForm({ ...ruleForm, label: e.target.value })}
            sx={{ minWidth: 190 }}
          />
          <Button variant="contained" onClick={saveRule}>
            {editingRuleId ? "Save" : "Add"}
          </Button>
          {editingRuleId && (
            <Button
              variant="outlined"
              onClick={() => {
                setEditingRuleId(null);
                setRuleForm(emptyRule);
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
        <TableContainer sx={{ maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {abbreviations.map(rule => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.type}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{rule.code}</TableCell>
                  <TableCell>{rule.label}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => editRule(rule)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => deleteRule(rule)}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {["Operational Name", "Platform", "Main Account", "Courier", "Status", "Actions"].map(header => (
                <TableCell key={header} sx={{ backgroundColor: "#5A5A5A", color: "white", fontWeight: "bold" }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((account, index) => (
              <TableRow
                key={account.id}
                sx={{
                  backgroundColor: account.is_active
                    ? (index % 2 === 0 ? "#f5f5f5" : "#e0e0e0")
                    : "#fff3f3",
                  opacity: account.is_active ? 1 : 0.75,
                }}
              >
                {editingId === account.id ? (
                  <>
                    <TableCell>
                      <TextField
                        size="small"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </TableCell>
                    <TableCell colSpan={3}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" onClick={() => inferFromName(editForm, setEditForm)}>
                          Use Name Rules
                        </Button>
                        {renderHierarchyInputs(editForm, setEditForm)}
                      </Stack>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>{account.name}</TableCell>
                    <TableCell>{account.platform_name || "-"}</TableCell>
                    <TableCell>{account.account_label || account.name}</TableCell>
                    <TableCell>{account.courier_name || "-"}</TableCell>
                  </>
                )}
                <TableCell>
                  <Chip
                    label={account.is_active ? "Active" : "Inactive"}
                    color={account.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {editingId === account.id ? (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={() => saveEdit(account.id)}>Save</Button>
                      <Button size="small" variant="outlined" onClick={() => setEditingId(null)}>Cancel</Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" onClick={() => startEdit(account)}>Edit</Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={account.is_active ? "warning" : "success"}
                        onClick={() => toggleActive(account.id, account.is_active, account.name)}
                      >
                        {account.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      {!account.is_active && (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => removeAccount(account.id, account.name)}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 2, color: "gray" }}>
        Operational names stay unchanged for uploads and scanner validation. Graphs can group by platform,
        main account, courier partner, or raw operational account.
      </Typography>
    </Box>
  );
};

export default AccountsPage;
